import { Form, FormInstance } from 'antd';
import { Button, Col, Colors, IcMenuChange, IcShow, IcTrash, Row, Select, Text } from 'basic-ui-lib';
import MainZone from 'basic-ui-lib/components/screen/MainZone';
import MainZoneTicket from 'basic-ui-lib/components/screen/MainZoneTicket';
import ThreeZone from 'basic-ui-lib/components/screen/ThreeZone';
import styled from 'styled-components';
import { FormValues } from '../ChannelForm';
import TwoZone from 'basic-ui-lib/components/screen/TwoZone';
import Portrait from 'basic-ui-lib/components/screen/Portrait';
import ThreePortraits from 'basic-ui-lib/components/screen/ThreePortraits';
import useGetListLayouts from 'modules/channel/services/hooks/useGetListLayouts';
import { LayoutCode } from 'modules/channel/services/constants';
import { useMemo } from 'react';

interface ScreenChannelProps {
  form?: FormInstance<FormValues>;
  idChannel?: string;
}

const layoutRenderMap: Record<string, (props: any) => React.ReactNode> = {
  [LayoutCode.MAIN_ONLY]: (props) => <MainZone border={4} {...props} />,
  [LayoutCode.MAIN_TICKER_BOTTOM]: (props) => <MainZoneTicket border={4} gap={8} {...props} />,
  [LayoutCode.LEFT_RIGHT]: (props) => <TwoZone border={4} gap={8} {...props} />,
  [LayoutCode.MAIN_RIGHT_BOTTOM]: (props) => <ThreeZone border={4} gap={8} {...props} />,
  [LayoutCode.THREE_PORTRAITS]: (props) => <ThreePortraits border={4} gap={8} {...props} />,
  [LayoutCode.MAIN_PORTRAIT]: (props) => <Portrait border={4} {...props} />,
};

const layoutOptionMap: Record<LayoutCode, React.ReactNode> = {
  [LayoutCode.MAIN_ONLY]: <MainZone />,
  [LayoutCode.MAIN_TICKER_BOTTOM]: <MainZoneTicket />,
  [LayoutCode.LEFT_RIGHT]: <TwoZone />,
  [LayoutCode.MAIN_RIGHT_BOTTOM]: <ThreeZone />,
  [LayoutCode.THREE_PORTRAITS]: <ThreePortraits />,
  [LayoutCode.MAIN_PORTRAIT]: <Portrait />,
};

const ScreenChannel = ({ form, idChannel }: ScreenChannelProps) => {
  const [dataLayouts] = useGetListLayouts();

  const layoutIdToCodeMap = useMemo(() => {
    const map: Record<string, LayoutCode> = {};
    dataLayouts?.forEach((layout) => {
      map[layout.layoutId] = layout.layoutCode;
    });
    return map;
  }, [dataLayouts]);

  const options = dataLayouts?.map((layout) => ({
    label: (
      <Row alignItems='center' width={100} height={30}>
        {layoutOptionMap[layout.layoutCode]}
        <Text title_h9 medium>
          {layout.name}
        </Text>
      </Row>
    ),
    value: layout.layoutId,
  })) || [];

  const handleOnChange = () => {
    form?.setFieldsValue({ activeZone: undefined, contentItems: undefined });
  };

  const handleDelete = () => {};

  return (
    <Container>
      <Row padding='16px 16px 8px'>
        <Form.Item name='layoutId' noStyle>
          <Select options={options} className='custom-select' onChange={handleOnChange} />
        </Form.Item>
        <ResolutionBox>
          <Col gap={0} flex={1}>
            <Text body_1 medium color='neutral.text.3' left>
              Resolution
            </Text>
            <Row gap={2}>
              <Text content_2 medium left>
                Full HD
              </Text>
              <Text body_1 medium color='neutral.text.3' left>
                (1920 x 1080)
              </Text>
            </Row>
          </Col>
          <button className='' style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <IcMenuChange />
          </button>
        </ResolutionBox>
        <Button type_primary prefix={<IcShow width={20} height={20} fill={Colors.neutral.bg[1]} />} lg>
          Preview
        </Button>
        {idChannel && (
          <Button type_delete prefix={<IcTrash width={20} height={20} fill={Colors.other[3]} />} onClick={handleDelete} lg>
            Delete
          </Button>
        )}
      </Row>
      <Content>
        <Form.Item noStyle shouldUpdate>
          {() => {
            const layoutId = form?.getFieldValue('layoutId');
            const layoutCode = layoutIdToCodeMap[layoutId];

            const contentItems = form?.getFieldValue('contentItems');
            const activeZone = form?.getFieldValue('activeZone');

            return layoutRenderMap[layoutCode]?.({
              value: contentItems,
              activeZone,
              onSelectZone: (zone: string) => form?.setFieldsValue({ activeZone: zone }),
            });
          }}
        </Form.Item>
      </Content>
    </Container>
  );
};

export default ScreenChannel;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .custom-select {
    flex: 1;
    height: 64px;
  }
`;

const ResolutionBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ccc;
  padding: 0 16px;
  flex: 1;
  height: 64px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
  border-radius: 4px;
`;

const Content = styled.div`
  padding: 0 40px 16px;
  flex: 1;
`;
